import { Grid2 as Grid, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Typography, useMediaQuery } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "@mui/styles"
import { useContext, useEffect, useState } from "react";
import { CreditReversalModalContent } from "./CreditReversalModalContent"
import { CreditRequestDetailModel } from "@/pages/CreditRequest/CreditRequestDetail";
import { UserContext } from "@/providers/user";
import { StaffProfile } from "@/infrastructure/auth/authConfig";
import { LargeActionButton } from "../Common/LargeActionButton";
import { LargeActionLink } from "../Common/LargeActionLink";
import {
    fullScreenSecondaryModalActionStyles,
    primaryModalActionStyles,
    secondaryModalActionStyles,
} from "../Common/modalActionStyles";
import { CreditRequestReversalRequest, ReversalItem } from "@/common/api/Models/types";
import { postCreditReversal } from "@/services/apiHelper";
import { getReversibleQuantity } from "@/util/creditReversalUtil";
import { ModalAlertType, ModalWithActionButton } from "../Modal/ModalWithActionButton";
import { MOBILE_MODAL_MEDIA_QUERY } from "../Modal/Modal.constants";

export interface CreateCreditReversalModalProps {
    isOpen: boolean;
    onModalClose: () => void;
    creditRequest: CreditRequestDetailModel;
}

type ResultModalState = {
    isOpen: boolean;
    title: string;
    message: string;
    alertType: ModalAlertType;
    primaryButtonName: string;
};

const resultModalStates: Record<"closed" | "success" | "failed", ResultModalState> = {
    closed: {
        isOpen: false,
        title: "",
        message: "",
        alertType: "info",
        primaryButtonName: "Ok",
    },
    success: {
        isOpen: true,
        title: "Success",
        message: "The credit reversal has been created successfully.",
        alertType: "success",
        primaryButtonName: "Ok",
    },
    failed: {
        isOpen: true,
        title: "Error",
        message: "Failed to create credit reversal, please try again.",
        alertType: "error",
        primaryButtonName: "Ok",
    },
};

const getValidCreditLines = (creditRequest: CreditRequestDetailModel) =>
    creditRequest.lineItems.filter((li) => getReversibleQuantity(li) > 0);

const buildRequest = (creditRequest: CreditRequestDetailModel, graphProfile: StaffProfile): CreditRequestReversalRequest => ({
    reversalItems: getValidCreditLines(creditRequest).map((li) => ({
        sku: li.sku,
        quantity: getReversibleQuantity(li),
        reason: "",
    })),
    userId: graphProfile.staffId,
    userName: graphProfile.displayName,
});

export const CreateCreditReversalModal: React.FC<CreateCreditReversalModalProps> = ({
    isOpen,
    onModalClose,
    creditRequest
}) => {
    const classes = useStyles();
    const mobileView = useMediaQuery(MOBILE_MODAL_MEDIA_QUERY);
    const { graphProfile } = useContext(UserContext);
    const validCreditLines = getValidCreditLines(creditRequest);
    const [request, setRequest] = useState<CreditRequestReversalRequest>(() => buildRequest(creditRequest, graphProfile));
    const [resultModal, setResultModal] = useState<ResultModalState>(resultModalStates.closed);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setRequest(buildRequest(creditRequest, graphProfile));
        }
    }, [isOpen, creditRequest, graphProfile]);

    const updateReversalItem = (sku: string, changes: Partial<ReversalItem>) => {
        const line = validCreditLines.find((li) => li.sku === sku);
        const clampedChanges =
            changes.quantity !== undefined && line
                ? { ...changes, quantity: Math.min(changes.quantity, getReversibleQuantity(line)) }
                : changes;
        setRequest((prev) => ({
            ...prev,
            reversalItems: prev.reversalItems.map((item) => (item.sku === sku ? { ...item, ...clampedChanges } : item)),
        }));
    };

    const copyReasonToAllItems = (reason: string) => {
        setRequest((prev) => ({
            ...prev,
            reversalItems: prev.reversalItems.map((item) => ({ ...item, reason })),
        }));
    };

    const onSubmitCreditReversal = async () => {
        setIsSubmitting(true);
        try {
            await postCreditReversal(request, creditRequest.id);
            setResultModal(resultModalStates.success);
        } catch (e) {
            setResultModal(resultModalStates.failed);
        } finally {
            setIsSubmitting(false);
        }
    };

    const onResultModalClose = () => {
        setResultModal((prev) => ({ ...prev, isOpen: false }));
        if (resultModal.alertType === "success") {
            onModalClose();
        }
    };

    const isMissingReason = request.reversalItems.some((item) => !item.reason);
    const isZeroTotalQuantity = request.reversalItems.reduce((total, item) => total + (item.quantity || 0), 0) === 0;

    return (
    <>
        <Dialog
            open={isOpen}
            fullScreen={mobileView}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle className={classes.dialogTitle}>
                <Grid container justifyContent="space-between" alignItems="center" wrap="nowrap">
                    <Grid>
                        <Typography variant="h5" className={classes.heading}>
                            Create Credit Reversal
                        </Typography>
                    </Grid>
                    <Grid>
                        <IconButton onClick={onModalClose} disabled={isSubmitting} aria-label="close">
                            <CloseIcon className={classes.closeIcon} />
                        </IconButton>
                    </Grid>
                </Grid>
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <DialogContentText className={classes.dialogContentMessage}>
                    From the SKU(s) listed below, select the qty you want to reverse the credit
                </DialogContentText>
                <CreditReversalModalContent
                    lineItems={validCreditLines}
                    reversalItems={request.reversalItems}
                    updateReversalItem={updateReversalItem}
                    copyReasonToAll={copyReasonToAllItems}
                />
            </DialogContent>
            <DialogActions>
                <Grid container direction="column" spacing={1} sx={{ width: "100%", px: 2 }}>
                    <Grid size={12} container justifyContent="center" alignItems="center" sx={{ width: "100%" }}>
                        <LargeActionButton
                            onClick={onSubmitCreditReversal}
                            sx={primaryModalActionStyles}
                            disabledRules={[
                                { when: isSubmitting, message: "Creating credit reversal..." },
                                { when: isZeroTotalQuantity, message: "Enter a quantity to reverse for at least one SKU" },
                                { when: isMissingReason, message: "Select a reason for each SKU to continue" },
                            ]}
                        >
                            Create Credit Reversal
                        </LargeActionButton>
                    </Grid>
                    <Grid size={12} container justifyContent="center" alignItems="center" sx={{ width: "100%" }}>
                        <LargeActionLink
                            onClick={onModalClose}
                            disabled={isSubmitting}
                            className={classes.cancelLink}
                            sx={mobileView ? fullScreenSecondaryModalActionStyles : secondaryModalActionStyles}
                        >
                            Cancel and return to credit
                        </LargeActionLink>
                    </Grid>
                </Grid>
            </DialogActions>
        </Dialog>
        <ModalWithActionButton {...resultModal} onPrimaryButtonClick={onResultModalClose} />
    </>
    )
}

const useStyles = makeStyles((theme) => ({
    dialogTitle: {
		paddingBottom: theme.spacing(2),
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2),
	},
	heading: {
		fontWeight: 700,
		fontSize: "1.3rem",
	},
	closeIcon: {
		fontSize: 30,
		position: "absolute",
		right: theme.spacing(-1),
		top: theme.spacing(-1.2),
	},
	dialogContent: {
		marginBottom: theme.spacing(0),
		paddingTop: theme.spacing(0),
		paddingBottom: theme.spacing(0),
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2),
	},
	dialogContentMessage: {
		color: theme.palette.text.primary,
		fontSize: "0.875rem",
	},
	cancelLink: {
		fontWeight: 400,
	},
}))